import type { BuildingModel, PlacedWall, Vec2 } from '@bw/building';
import { contrastColour, darken } from './theme.js';
import type { DrawTheme } from './theme.js';
import type { Viewport } from './viewport.js';

/**
 * The plan view (phase 2, M5).
 *
 * A top-down drawing of the same walls the elevation view shows edge-on. It
 * reuses the existing viewport unchanged by plotting world (x, z) as (x, -y):
 * screen X is world X, and screen Y grows as world Z does, which is what
 * looking down the -Y axis gives you. Pan, zoom and fit therefore all work
 * without a second transform to keep in step.
 */

export interface PlanScene {
  readonly building: BuildingModel;
  /**
   * The wall being worked on. There used to be a second id here for the wall
   * picked in the plan, drawn with the selection outline while this one only
   * got a stronger fill. They are the same wall now, so one id draws both.
   */
  readonly activeWallId: string;
  readonly colourOfMaterial: (materialId: string) => string;
  /** Where a drag would put the wall, drawn as a ghost while it is moving. */
  readonly ghost?: readonly Vec2[] | undefined;
  readonly ghostValid?: boolean | undefined;
  /** Plan positions the current drag is snapping to. */
  readonly snapMarks?: readonly Vec2[] | undefined;
}

/** How far outside the wall the rotate grip sits, in screen pixels. */
export const ROTATE_HANDLE_PX = 26;
export const ROTATE_HANDLE_RADIUS_PX = 7;

/**
 * Where the rotate grip for a wall sits on screen.
 *
 * Shared by the painter and the controller's hit test, so the thing you can
 * see and the thing you can grab can never drift apart.
 */
export function rotateHandleAt(
  viewport: Viewport,
  wall: PlacedWall,
): { x: number; y: number } | null {
  if (wall.planCorners.length < 4) return null;
  const a = planToScreen(viewport, wall.planCorners[1] as Vec2);
  const b = planToScreen(viewport, wall.planCorners[2] as Vec2);
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  // Push out along the wall's own direction, past its far end.
  const c = planToScreen(viewport, wall.planCorners[0] as Vec2);
  const dx = mid.x - c.x;
  const dy = mid.y - c.y;
  const length = Math.hypot(dx, dy) || 1;
  return {
    x: mid.x + (dx / length) * ROTATE_HANDLE_PX,
    y: mid.y + (dy / length) * ROTATE_HANDLE_PX,
  };
}

/** World plan point to screen. The one place the plan projection lives. */
export function planToScreen(viewport: Viewport, at: Vec2): { x: number; y: number } {
  return { x: viewport.toScreenX(at.x), y: viewport.toScreenY(-at.z) };
}

/** Screen back to world plan. */
export function screenToPlan(viewport: Viewport, x: number, y: number): Vec2 {
  return { x: viewport.toWorldX(x), z: -viewport.toWorldY(y) };
}

export function paintPlan(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  scene: PlanScene,
): void {
  const { building } = scene;
  ctx.save();
  ctx.lineJoin = 'round';

  paintFootprint(ctx, viewport, theme, scene);

  for (const wall of building.walls) {
    paintWall(ctx, viewport, theme, scene, wall);
  }

  paintOverlaps(ctx, viewport, theme, scene);

  if (scene.ghost !== undefined && scene.ghost.length > 0) {
    ctx.beginPath();
    trace(ctx, viewport, scene.ghost);
    ctx.fillStyle = scene.ghostValid === false ? theme.previewInvalid : theme.previewValid;
    ctx.globalAlpha = 0.22;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = scene.ghostValid === false ? theme.previewInvalid : theme.previewValid;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  for (const mark of scene.snapMarks ?? []) {
    const at = planToScreen(viewport, mark);
    ctx.strokeStyle = theme.snapGuide;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(at.x - 6, at.y);
    ctx.lineTo(at.x + 6, at.y);
    ctx.moveTo(at.x, at.y - 6);
    ctx.lineTo(at.x, at.y + 6);
    ctx.stroke();
  }

  ctx.restore();
}

function trace(ctx: CanvasRenderingContext2D, viewport: Viewport, ring: readonly Vec2[]): void {
  ring.forEach((corner, index) => {
    const at = planToScreen(viewport, corner);
    if (index === 0) ctx.moveTo(at.x, at.y);
    else ctx.lineTo(at.x, at.y);
  });
  ctx.closePath();
}

/**
 * The building outline, or the ends that stop it being one.
 *
 * An open footprint is the single most useful thing this view can tell you, so
 * it is drawn loudly: every dangling wall end gets a ring around it.
 */
function paintFootprint(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  scene: PlanScene,
): void {
  const { footprint } = scene.building;
  if (footprint.closed && footprint.ring.length >= 3) {
    ctx.beginPath();
    trace(ctx, viewport, footprint.ring);
    ctx.fillStyle = theme.shapeWall;
    ctx.fill();
    ctx.strokeStyle = theme.sketchWall;
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
    return;
  }
  for (const end of footprint.openEnds) {
    const at = planToScreen(viewport, end);
    ctx.beginPath();
    ctx.arc(at.x, at.y, 7, 0, Math.PI * 2);
    ctx.strokeStyle = theme.previewInvalid;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

function paintWall(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  scene: PlanScene,
  wall: PlacedWall,
): void {
  const selected = wall.id === scene.activeWallId;

  if (wall.planCorners.length < 4) {
    // A wall with no bricks and no envelope has no length. Say so rather than
    // drawing a zero-width sliver that cannot be grabbed.
    const at = planToScreen(viewport, {
      x: wall.placement.origin.x,
      z: wall.placement.origin.z,
    });
    ctx.beginPath();
    ctx.arc(at.x, at.y, 5, 0, Math.PI * 2);
    ctx.strokeStyle = selected ? theme.selectionOutline : theme.hintSoft;
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.setLineDash([]);
    label(ctx, theme, at.x + 9, at.y + 3, `${wall.name} · empty`, theme.hintSoft);
    return;
  }

  const fill = scene.colourOfMaterial(wall.source.baseMaterialId);
  ctx.beginPath();
  trace(ctx, viewport, wall.planCorners);
  ctx.fillStyle = fill;
  ctx.globalAlpha = selected ? 0.85 : 0.6;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = selected ? theme.selectionOutline : darken(fill, 0.35);
  ctx.lineWidth = selected ? 2 : 1;
  ctx.stroke();

  // The outer face — local z = 0, the elevation you drew — as a heavy line, so
  // which way a wall faces is readable at a glance rather than inferred.
  const a = planToScreen(viewport, wall.planCorners[0] as Vec2);
  const b = planToScreen(viewport, wall.planCorners[1] as Vec2);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.strokeStyle = selected ? theme.selectionOutline : darken(fill, 0.5);
  ctx.lineWidth = selected ? 3.5 : 2.5;
  ctx.stroke();

  const centre = {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  };
  if (Math.hypot(b.x - a.x, b.y - a.y) > 46) {
    label(ctx, theme, centre.x, centre.y, wall.name, contrastColour(fill), fill);
  }

  if (selected) {
    const handle = rotateHandleAt(viewport, wall);
    if (handle !== null) {
      ctx.beginPath();
      ctx.moveTo(centre.x, centre.y);
      ctx.lineTo(handle.x, handle.y);
      ctx.strokeStyle = theme.selectionOutline;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, ROTATE_HANDLE_RADIUS_PX, 0, Math.PI * 2);
      ctx.fillStyle = theme.background;
      ctx.fill();
      ctx.strokeStyle = theme.selectionOutline;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }
}

function paintOverlaps(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  scene: PlanScene,
): void {
  if (scene.building.overlaps.length === 0) return;
  ctx.strokeStyle = theme.previewInvalid;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 3]);
  for (const overlap of scene.building.overlaps) {
    // The centre of the shared region itself, so the ring lands on the corner
    // it is talking about rather than somewhere between the two walls.
    const at = planToScreen(viewport, overlap.at);
    ctx.beginPath();
    ctx.arc(at.x, at.y, 9, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

function label(
  ctx: CanvasRenderingContext2D,
  theme: DrawTheme,
  x: number,
  y: number,
  text: string,
  colour: string,
  ground?: string,
): void {
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (ground !== undefined) {
    const width = ctx.measureText(text).width + 8;
    ctx.fillStyle = ground;
    ctx.globalAlpha = 0.9;
    ctx.fillRect(x - width / 2, y - 8, width, 16);
    ctx.globalAlpha = 1;
  }
  ctx.fillStyle = colour;
  ctx.fillText(text, x, y);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  void theme;
}
